import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field, Loading, PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";

const inputCls = "bg-white border border-border focus:border-gold px-3 py-2.5 w-full outline-none transition text-sm rounded-none";

function ListEditor({ label, items, onChange, fields, testId }) {
  const update = (i, key, val) => {
    const next = [...items];
    next[i] = fields.length === 1 ? val : { ...next[i], [key]: val };
    onChange(next);
  };
  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-widest text-navy-700">{label}</label>
        <button type="button" onClick={() => onChange([...items, fields.length === 1 ? "" : Object.fromEntries(fields.map((f) => [f.key, ""]))])}
          className="inline-flex items-center gap-1 text-xs font-semibold text-gold-700 hover:text-navy transition-colors" data-testid={`${testId}-add`}>
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>
      {items.map((item, i) => (
        <div key={i} className="flex gap-2 items-start">
          {fields.length === 1 ? (
            <input className={inputCls} value={item} onChange={(e) => update(i, null, e.target.value)}
              placeholder={fields[0].placeholder} data-testid={`${testId}-item-${i}`} />
          ) : (
            <div className="grid grid-cols-2 gap-2 flex-1">
              {fields.map((f) => (
                <input key={f.key} className={inputCls} value={item[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)}
                  placeholder={f.placeholder} data-testid={`${testId}-item-${i}-${f.key}`} />
              ))}
            </div>
          )}
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))}
            className="p-2.5 text-muted-foreground hover:text-red-600 transition-colors shrink-0" data-testid={`${testId}-remove-${i}`} aria-label="Remove">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function ProfileAdmin() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const set = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });
  const setStat = (k) => (e) => setProfile({ ...profile, stats: { ...profile.stats, [k]: e.target.value } });
  const setSocial = (k) => (e) => setProfile({ ...profile, social: { ...profile.social, [k]: e.target.value } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", profile);
      toast.success("Profile saved — public website updated");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <Loading label="Loading profile" />;

  return (
    <div data-testid="profile-admin-page">
      <PageHeader title="Attorney Profile" description="Everything here powers the public website." testId="profile-admin-header">
        <Button onClick={save} disabled={saving} data-testid="profile-save-btn"
          className="bg-navy text-ivory hover:bg-navy-800 rounded-none uppercase tracking-wider text-xs font-semibold">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save Profile</>}
        </Button>
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white border border-border p-6 sm:p-8" data-testid="profile-basic-section">
            <h2 className="font-serif text-2xl font-semibold text-navy mb-6">Basic Information</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name" htmlFor="p-name"><input id="p-name" className={inputCls} value={profile.name} onChange={set("name")} data-testid="profile-name-input" /></Field>
              <Field label="Title" htmlFor="p-title"><input id="p-title" className={inputCls} value={profile.title} onChange={set("title")} data-testid="profile-title-input" /></Field>
              <Field label="Firm Name" htmlFor="p-firm"><input id="p-firm" className={inputCls} value={profile.firm_name} onChange={set("firm_name")} data-testid="profile-firm-input" /></Field>
              <Field label="Years of Experience" htmlFor="p-years"><input id="p-years" type="number" className={inputCls} value={profile.years_experience} onChange={(e) => setProfile({ ...profile, years_experience: Number(e.target.value) })} data-testid="profile-years-input" /></Field>
              <div className="sm:col-span-2">
                <Field label="Tagline" htmlFor="p-tagline"><input id="p-tagline" className={inputCls} value={profile.tagline} onChange={set("tagline")} data-testid="profile-tagline-input" /></Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Short Bio (homepage)" htmlFor="p-short"><textarea id="p-short" rows={3} className={`${inputCls} resize-none`} value={profile.about_short} onChange={set("about_short")} data-testid="profile-short-bio-input" /></Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Full Biography (paragraphs separated by blank lines)" htmlFor="p-bio"><textarea id="p-bio" rows={8} className={`${inputCls} resize-none`} value={profile.bio} onChange={set("bio")} data-testid="profile-bio-input" /></Field>
              </div>
            </div>
          </section>

          <section className="bg-white border border-border p-6 sm:p-8">
            <h2 className="font-serif text-2xl font-semibold text-navy mb-6">Credentials</h2>
            <div className="space-y-8">
              <ListEditor label="Qualifications" items={profile.qualifications || []} onChange={(v) => setProfile({ ...profile, qualifications: v })}
                fields={[{ key: "text", placeholder: "e.g. Juris Doctor, Harvard Law School (2007)" }]} testId="profile-qualifications" />
              <ListEditor label="Education" items={profile.education || []} onChange={(v) => setProfile({ ...profile, education: v })}
                fields={[{ key: "degree", placeholder: "Degree" }, { key: "school", placeholder: "School" }, { key: "year", placeholder: "Year" }, { key: "detail", placeholder: "Detail" }]} testId="profile-education" />
              <ListEditor label="Experience" items={profile.experience || []} onChange={(v) => setProfile({ ...profile, experience: v })}
                fields={[{ key: "role", placeholder: "Role" }, { key: "firm", placeholder: "Firm" }, { key: "period", placeholder: "Period" }, { key: "detail", placeholder: "Detail" }]} testId="profile-experience" />
              <ListEditor label="Awards" items={profile.awards || []} onChange={(v) => setProfile({ ...profile, awards: v })}
                fields={[{ key: "title", placeholder: "Award title" }, { key: "year", placeholder: "Year / Issuer" }]} testId="profile-awards" />
              <ListEditor label="Bar Admissions" items={profile.bar_admissions || []} onChange={(v) => setProfile({ ...profile, bar_admissions: v })}
                fields={[{ key: "text", placeholder: "e.g. Illinois State Bar (2007)" }]} testId="profile-bar" />
              <ListEditor label="Languages" items={profile.languages || []} onChange={(v) => setProfile({ ...profile, languages: v })}
                fields={[{ key: "text", placeholder: "e.g. English" }]} testId="profile-languages" />
            </div>
          </section>

          <section className="bg-white border border-border p-6 sm:p-8">
            <h2 className="font-serif text-2xl font-semibold text-navy mb-6">Practice Areas</h2>
            <ListEditor label="Areas of practice" items={profile.practice_areas || []} onChange={(v) => setProfile({ ...profile, practice_areas: v })}
              fields={[{ key: "title", placeholder: "Title (e.g. Corporate Law)" }, { key: "icon", placeholder: "Icon (Briefcase, Scale…)" }, { key: "id", placeholder: "id (kebab-case)" }, { key: "description", placeholder: "Description" }]}
              testId="profile-practice-areas" />
          </section>
        </div>

        <div className="space-y-6">
          <section className="bg-white border border-border p-6" data-testid="profile-photo-section">
            <h2 className="font-serif text-xl font-semibold text-navy mb-5">Photo</h2>
            {profile.photo_url && <img src={profile.photo_url} alt="Attorney" className="w-full h-56 object-cover object-top border border-border mb-4" data-testid="profile-photo-preview" />}
            <Field label="Photo URL" htmlFor="p-photo"><input id="p-photo" className={inputCls} value={profile.photo_url} onChange={set("photo_url")} data-testid="profile-photo-input" /></Field>
            <div className="mt-4">
              <Field label="Secondary Photo URL (about section)" htmlFor="p-photo2"><input id="p-photo2" className={inputCls} value={profile.photo_alt_url || ""} onChange={set("photo_alt_url")} data-testid="profile-photo-alt-input" /></Field>
            </div>
          </section>

          <section className="bg-white border border-border p-6">
            <h2 className="font-serif text-xl font-semibold text-navy mb-5">Contact & Office</h2>
            <div className="space-y-4">
              <Field label="Email" htmlFor="p-email"><input id="p-email" className={inputCls} value={profile.email} onChange={set("email")} data-testid="profile-email-input" /></Field>
              <Field label="Phone" htmlFor="p-phone"><input id="p-phone" className={inputCls} value={profile.phone} onChange={set("phone")} data-testid="profile-phone-input" /></Field>
              <Field label="Office Address" htmlFor="p-address"><input id="p-address" className={inputCls} value={profile.office_address} onChange={set("office_address")} data-testid="profile-address-input" /></Field>
              <Field label="Office Hours" htmlFor="p-hours"><input id="p-hours" className={inputCls} value={profile.office_hours} onChange={set("office_hours")} data-testid="profile-hours-input" /></Field>
            </div>
          </section>

          <section className="bg-white border border-border p-6">
            <h2 className="font-serif text-xl font-semibold text-navy mb-5">Headline Stats</h2>
            <div className="space-y-4">
              <Field label="Cases Handled" htmlFor="p-stat-cases"><input id="p-stat-cases" className={inputCls} value={profile.stats?.cases_handled || ""} onChange={setStat("cases_handled")} data-testid="profile-stat-cases-input" /></Field>
              <Field label="Success Rate" htmlFor="p-stat-rate"><input id="p-stat-rate" className={inputCls} value={profile.stats?.success_rate || ""} onChange={setStat("success_rate")} data-testid="profile-stat-rate-input" /></Field>
              <Field label="Clients Served" htmlFor="p-stat-clients"><input id="p-stat-clients" className={inputCls} value={profile.stats?.clients_served || ""} onChange={setStat("clients_served")} data-testid="profile-stat-clients-input" /></Field>
              <Field label="Recovered" htmlFor="p-stat-recovered"><input id="p-stat-recovered" className={inputCls} value={profile.stats?.settlements_recovered || ""} onChange={setStat("settlements_recovered")} data-testid="profile-stat-recovered-input" /></Field>
            </div>
          </section>

          <section className="bg-white border border-border p-6">
            <h2 className="font-serif text-xl font-semibold text-navy mb-5">Social Links</h2>
            <div className="space-y-4">
              <Field label="LinkedIn" htmlFor="p-linkedin"><input id="p-linkedin" className={inputCls} value={profile.social?.linkedin || ""} onChange={setSocial("linkedin")} data-testid="profile-linkedin-input" /></Field>
              <Field label="Twitter / X" htmlFor="p-twitter"><input id="p-twitter" className={inputCls} value={profile.social?.twitter || ""} onChange={setSocial("twitter")} data-testid="profile-twitter-input" /></Field>
              <Field label="Facebook" htmlFor="p-facebook"><input id="p-facebook" className={inputCls} value={profile.social?.facebook || ""} onChange={setSocial("facebook")} data-testid="profile-facebook-input" /></Field>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
